'use client';

import type React from 'react';
import { useState, useEffect } from 'react';

const promoCards = [
  {
    title: 'Master Your Money with CashBitAI',
    description: 'Gain full control of your finances using smart, AI-powered tools from CashBitAI.',
    button: 'Get Started',
    accent: 'bg-green-950 text-white',
    image: 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png',
    dark: false
  },
  {
    title: 'Take Control of Your Finances with CashBitAI',
    description: 'Track, manage, and grow your money effortlessly with intelligent insights.',
    button: 'Try It Now',
    accent: 'bg-gray-100 text-gray-600 dark:bg-green-950/90 dark:text-gray-100',
    image: 'https://cdn-icons-png.flaticon.com/512/1170/1170576.png',
    dark: false
  },
  {
    title: 'Smarter Spending Starts with CashBitAI',
    description: 'Make better financial decisions with personalized AI-driven advice.',
    button: 'Start Saving',
    accent: 'bg-green-950 text-white',
    image: 'https://cdn-icons-png.flaticon.com/512/3523/3523887.png',
    dark: true
  }
];

const marketingLines = [
  'CashBitAI: Where Your Money Works Smarter',
  'Level Up Your Finances with CashBitAI',
  'Turn Every Dollar into a Smart Move — CashBitAI',
  'Your AI-Powered Path to Wealth Starts Here',
  'Reimagine Your Financial Future with CashBitAI',
  'Let CashBitAI Take the Stress Out of Money',
  'AI Meets Finance — Welcome to CashBitAI',
  'From Budgeting to Investing, Do It All with CashBitAI'
];

const creamBg = 'bg-[#FFFDF6] dark:bg-[#0F100B]';

// Floating Objects Component
const FloatingObjects: React.FC = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating Dollar Signs */}
      <div
        className="absolute top-20 left-10 text-green-300/30 dark:text-green-600/20 text-2xl animate-bounce"
        style={{ animationDelay: '0s', animationDuration: '3s' }}
      >
        $
      </div>
      <div
        className="absolute top-40 right-16 text-green-400/40 dark:text-green-500/30 text-xl animate-bounce"
        style={{ animationDelay: '1s', animationDuration: '4s' }}
      >
        $
      </div>
      <div
        className="absolute bottom-32 left-20 text-green-500/30 dark:text-green-400/20 text-lg animate-bounce"
        style={{ animationDelay: '2s', animationDuration: '3.5s' }}
      >
        $
      </div>

      {/* Floating Geometric Shapes */}
      <div
        className="absolute top-16 right-8 w-4 h-4 bg-green-300/20 dark:bg-green-600/15 rounded-full animate-pulse"
        style={{ animationDelay: '0.5s' }}
      ></div>
      <div
        className="absolute top-60 left-6 w-3 h-3 bg-green-400/25 dark:bg-green-500/20 rounded-full animate-pulse"
        style={{ animationDelay: '1.5s' }}
      ></div>
      <div
        className="absolute bottom-20 right-12 w-5 h-5 bg-green-500/20 dark:bg-green-400/15 rounded-full animate-pulse"
        style={{ animationDelay: '2.5s' }}
      ></div>

      {/* Floating Triangles */}
      <div
        className="absolute top-32 left-16 w-0 h-0 border-l-4 border-r-4 border-b-6 border-l-transparent border-r-transparent border-b-green-300/30 dark:border-b-green-600/20 animate-spin"
        style={{ animationDuration: '8s' }}
      ></div>
      <div
        className="absolute bottom-40 right-20 w-0 h-0 border-l-3 border-r-3 border-b-5 border-l-transparent border-r-transparent border-b-green-400/25 dark:border-b-green-500/20 animate-spin"
        style={{ animationDuration: '10s', animationDirection: 'reverse' }}
      ></div>

      {/* Floating Coins */}
      <div
        className="absolute top-24 right-24 w-6 h-6 bg-gradient-to-br from-green-300/30 to-green-500/30 dark:from-green-600/20 dark:to-green-400/20 rounded-full animate-ping"
        style={{ animationDelay: '1s' }}
      ></div>
      <div
        className="absolute bottom-28 left-8 w-4 h-4 bg-gradient-to-br from-green-400/25 to-green-600/25 dark:from-green-500/20 dark:to-green-300/20 rounded-full animate-ping"
        style={{ animationDelay: '3s' }}
      ></div>
    </div>
  );
};

const Hero: React.FC = () => {
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [isSignup, setIsSignup] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [signupForm, setSignupForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setTaglineIndex((prev) => (prev + 1) % marketingLines.length);
        setFade(true);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  const handleSignupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSignupForm({ ...signupForm, [e.target.name]: e.target.value });
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle login logic here
  };

  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle signup logic here
  };

  return (
    <section
      className={`relative min-h-screen flex items-center justify-center ${creamBg} py-12 transition-colors duration-300 overflow-hidden`}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200/20 dark:bg-green-800/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-green-300/20 dark:bg-green-700/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

        {/* Floating money particles */}
        <div
          className="absolute top-1/4 left-1/4 w-2 h-2 bg-green-400/30 rounded-full animate-ping"
          style={{ animationDelay: '2s' }}
        ></div>
        <div
          className="absolute top-3/4 right-1/4 w-3 h-3 bg-green-500/25 rounded-full animate-ping"
          style={{ animationDelay: '4s' }}
        ></div>
        <div
          className="absolute top-1/2 left-1/3 w-1 h-1 bg-green-600/40 rounded-full animate-ping"
          style={{ animationDelay: '6s' }}
        ></div>
      </div>

      <div className="w-full max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-8 items-center px-4 relative z-10">
        {/* Left: Auth Card */}
        <div
          className={`rounded-2xl shadow-xl p-8 flex flex-col min-h-[550px] justify-between border border-gray-200 dark:border-green-950 ${creamBg} md:col-span-2 transform transition-all duration-700 ${
            mounted ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          } hover:shadow-2xl hover:scale-[1.02] relative overflow-hidden`}
        >
          {/* Floating Objects inside the auth card */}
          <FloatingObjects />

          {/* Moving Girl Character */}

          <div className="relative z-10">
            <div className="transform transition-all duration-500 hover:scale-105">
              <h2 className="text-2xl font-bold text-green-950 dark:text-green-300 mb-2 animate-pulse">
                CashBitAI
              </h2>
            </div>
            <p
              className={`text-gray-600 dark:text-gray-300 mb-8 transition-all duration-500 ${
                mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
            >
              {isSignup ? 'Create your account below:' : 'Welcome back'}
            </p>

            {/* Auth Form */}
            <div
              className={`transition-all duration-500 transform ${
                isSignup ? 'translate-x-0 opacity-100' : 'translate-x-0 opacity-100'
              }`}
            >
              {!isSignup ? (
                <form className="space-y-5 mb-4" onSubmit={handleLogin}>
                  <div className="transform transition-all duration-300 hover:scale-[1.02]">
                    <label
                      className="block text-gray-700 dark:text-gray-200 mb-1 font-medium transition-colors duration-200"
                      htmlFor="login-email"
                    >
                      Email
                    </label>
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={loginForm.email}
                      onChange={handleLoginChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-green-900 bg-white dark:bg-green-950/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent transition-all duration-300 focus:scale-[1.02] hover:shadow-md"
                      placeholder="Enter your email"
                    />
                  </div>
                  <div className="transform transition-all duration-300 hover:scale-[1.02] delay-75">
                    <div className="flex justify-between items-center mb-1">
                      <label
                        className="block text-gray-700 dark:text-gray-200 font-medium"
                        htmlFor="login-password"
                      >
                        Password
                      </label>
                      <a
                        href="#"
                        className="text-sm text-green-700 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-all duration-200 hover:scale-105"
                      >
                        Forgot password?
                      </a>
                    </div>
                    <input
                      id="login-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={loginForm.password}
                      onChange={handleLoginChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-green-900 bg-white dark:bg-green-950/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent transition-all duration-300 focus:scale-[1.02] hover:shadow-md"
                      placeholder="Enter your password"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-green-800 hover:bg-green-900 text-white font-semibold text-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] hover:-translate-y-1"
                  >
                    Log In
                  </button>
                  <div className="text-center mt-4 transform transition-all duration-300 hover:scale-105">
                    <span className="text-gray-500 dark:text-gray-300">
                      Don't have an account?{' '}
                    </span>
                    <button
                      type="button"
                      className="text-green-800 dark:text-green-400 font-semibold hover:underline transition-all duration-200 hover:scale-105"
                      onClick={() => setIsSignup(true)}
                    >
                      Sign Up
                    </button>
                  </div>
                </form>
              ) : (
                <form className="space-y-4 mb-4" onSubmit={handleSignup}>
                  {[
                    {
                      id: 'signup-username',
                      name: 'username',
                      type: 'text',
                      label: 'Username',
                      autoComplete: 'username'
                    },
                    {
                      id: 'signup-email',
                      name: 'email',
                      type: 'email',
                      label: 'Email',
                      autoComplete: 'email'
                    },
                    {
                      id: 'signup-password',
                      name: 'password',
                      type: 'password',
                      label: 'Password',
                      autoComplete: 'new-password'
                    },
                    {
                      id: 'signup-confirm-password',
                      name: 'confirmPassword',
                      type: 'password',
                      label: 'Confirm Password',
                      autoComplete: 'new-password'
                    }
                  ].map((field, index) => (
                    <div
                      key={field.id}
                      className={`transform transition-all duration-300 hover:scale-[1.02]`}
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <label
                        className="block text-gray-700 dark:text-gray-200 mb-1"
                        htmlFor={field.id}
                      >
                        {field.label}
                      </label>
                      <input
                        id={field.id}
                        name={field.name}
                        type={field.type}
                        autoComplete={field.autoComplete}
                        required
                        value={signupForm[field.name as keyof typeof signupForm]}
                        onChange={handleSignupChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-green-900 bg-white dark:bg-green-950/20 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-700 focus:border-transparent transition-all duration-300 focus:scale-[1.02] hover:shadow-md"
                      />
                    </div>
                  ))}
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-green-800 hover:bg-green-900 text-white font-semibold text-lg shadow transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] hover:-translate-y-1"
                  >
                    Sign Up
                  </button>
                  <div className="text-center mt-2 transform transition-all duration-300 hover:scale-105">
                    <span className="text-gray-500 dark:text-gray-300">
                      Already have an account?{' '}
                    </span>
                    <button
                      type="button"
                      className="text-green-800 dark:text-green-400 font-semibold hover:underline transition-all duration-200 hover:scale-105"
                      onClick={() => setIsSignup(false)}
                    >
                      Log In instead
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
          <div
            className={`flex justify-center gap-4 text-xs text-gray-600 dark:text-gray-300 mt-8 transition-all duration-700 relative z-10 ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
          >
            <a
              href="#"
              className="hover:underline hover:text-green-800 dark:hover:text-green-400 transition-all duration-200 hover:scale-110"
            >
              Privacy
            </a>
            <span>|</span>
            <a
              href="#"
              className="hover:underline hover:text-green-800 dark:hover:text-green-400 transition-all duration-200 hover:scale-110"
            >
              Terms of service
            </a>
            <span>|</span>
            <a
              href="#"
              className="hover:underline hover:text-green-800 dark:hover:text-green-400 transition-all duration-200 hover:scale-110"
            >
              Help
            </a>
          </div>
        </div>

        {/* Right: Promo Cards Layout */}
        <div
          className={`flex flex-col gap-6 min-h-[600px] md:col-span-3 transform transition-all duration-700 ${
            mounted ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}
        >
          {/* Top Card */}
          <div
            className={`rounded-2xl shadow-md p-8 flex items-center gap-8 ${promoCards[0].accent} min-h-[250px] border border-gray-200 dark:border-green-900 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group`}
            style={{ animationDelay: '200ms' }}
          >
            {promoCards[0].image && (
              <img
                src={promoCards[0].image || '/placeholder.svg'}
                alt="icon"
                className="w-20 h-20 rounded-xl bg-white/10 p-3 shadow-md border border-white/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
              />
            )}
            <div className="flex-1">
              <h3 className="font-bold text-2xl mb-3 transition-all duration-300 group-hover:text-green-100">
                {promoCards[0].title}
              </h3>
              <p className="text-base mb-5 opacity-90 leading-relaxed transition-all duration-300 group-hover:opacity-100">
                {promoCards[0].description}
              </p>
              <button
                className={`px-6 py-3 rounded-lg font-medium shadow-md transition-all duration-300 bg-green-900 hover:bg-green-950 text-white hover:shadow-xl transform hover:scale-105 active:scale-95 hover:-translate-y-1`}
              >
                {promoCards[0].button}
              </button>
            </div>
          </div>

          {/* Animated Tagline Section */}
          <div className="h-14 flex items-center justify-center relative overflow-hidden my-2">
            <span
              className={`absolute left-0 right-0 text-center text-lg sm:text-xl font-medium transition-all duration-500 ease-in-out transform
                  ${fade ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-6 scale-95'}
                  text-gray-700 dark:text-gray-200
                `}
              key={taglineIndex}
            >
              {marketingLines[taglineIndex]}
            </span>
          </div>

          {/* Bottom Two Cards Side by Side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-grow">
            {[promoCards[1], promoCards[2]].map((card, idx) => (
              <div
                key={idx}
                className={`rounded-2xl shadow-md p-7 flex flex-col items-start justify-between ${card.accent} min-h-[260px] h-full border border-gray-200 dark:border-green-900 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group`}
                style={{ animationDelay: `${(idx + 1) * 300}ms` }}
              >
                <div className="flex-1 w-full flex flex-col">
                  {card.image && (
                    <img
                      src={card.image || '/placeholder.svg'}
                      alt="icon"
                      className={`w-16 h-16 mb-4 rounded-xl p-2 shadow-md border transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${
                        card.dark
                          ? 'bg-white/10 border-white/20'
                          : 'bg-white border-gray-200 dark:border-green-900'
                      }`}
                    />
                  )}
                  <h3 className="font-bold text-xl mb-3 transition-all duration-300 group-hover:scale-105">
                    {card.title}
                  </h3>
                  <p className="text-sm mb-4 opacity-90 leading-relaxed transition-all duration-300 group-hover:opacity-100">
                    {card.description}
                  </p>
                </div>
                <button
                  className={`px-5 py-2.5 rounded-lg font-medium shadow-md transition-all duration-300 w-full text-center transform hover:scale-105 active:scale-95 hover:-translate-y-1
                    ${
                      card.dark
                        ? 'bg-green-900 hover:bg-green-950 text-white'
                        : 'bg-green-800 hover:bg-green-900 text-white'
                    } hover:shadow-xl
                  `}
                >
                  {card.button}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
